import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	TSwissPostIntegrationStatusResponsePayload,
	TShipmentsStore,
	TSwissPostOrdersRequest,
	TSwissPostOrdersResponsePayload,
	TSwissPostOrderRequest,
	TSwissPostOrderResponsePayload,
	TSwissPostDownloadLabelRequest,
	TSwissPostDownloadLabelResponsePayload,
	TSwissPostDownloadProformaRequest,
	TSwissPostDownloadProformaResponsePayload,
	TSwissPostRequestDeliveryRequest,
	TSwissPostSendLastErrorByEmailRequest,
	TSwissPostUpdateHsCodeResponse,
	THsCodeDescriptionRequest,
	THsCodeDescriptionResponsePayload,
	TSwissPostUpdateOrderFieldValueRequest,
	TUpdateCustomsDescriptionRequest,
	TSwissPostUpdateWeightRequest,
	TSwissPostUploadProformaRequest,
	TSwissPostSignedProformaPreviewRequest,
	TSwissPostSignedProformaPreviewResponsePayload,
	TSwissPostFirstMileLabelRequest,
	TSwissPostFirstMileLabelResponsePayload,
} from './types';
// ==========================================:
export const initialState: TShipmentsStore = {
	isSwissPostIntegrationEnabled: false,
	isSwissPostIntegrationEnabledLoading: false,
	shipmentOrders: null,
	shipmentOrdersLoading: false,
	shipmentOrder: null,
	shipmentOrderError: null,
	shipmentOrderLoading: false,
	shipmentLabel: null,
	shipmentLabelLoading: false,
	shipmentProforma: null,
	shipmentProformaLoading: false,
	requestDeliveryLoading: false,
	requestDeliveryError: null,
	sendLastErrorByEmailLoading: false,
	sendLastErrorByEmailError: null,
	sendLastErrorByEmailSuccess: false,
	updateHsCodeLoading: false,
	hsCodeDescription: null,
	hsCodeDescriptionLoading: false,
	updateOrderFieldValueLoading: false,
	updateCustomsDescriptionsLoading: false,
	updateWeightLoading: false,
	updateWeightError: null,
	shipmentProformaUploadLoading: false,
	shipmentProformaUploadError: null,
	unsignedProforma: null,
	unsignedProformaLoading: false,
	signedProformaPreview: null,
	signedProformaPreviewLoading: false,
	firstMileLabel: null,
	firstMileLabelLoading: false,
	firstMileLabelErrorDialog: null,
	firstMileLabelError: null,
};

// ==========================================:
const shipments = createSlice({
	name: '@@shipments',
	initialState,
	reducers: {
		getIsSwissPostIntegrationEnabledRequest: (state: TShipmentsStore) => {
			const shipmentsState = state;
			shipmentsState.isSwissPostIntegrationEnabledLoading = true;
		},
		getIsSwissPostIntegrationEnabledSuccess: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostIntegrationStatusResponsePayload>,
		) => {
			const shipmentsState = state;
			shipmentsState.isSwissPostIntegrationEnabled =
				action.payload?.isSwissPostIntegrationEnabled ?? false;
			shipmentsState.isSwissPostIntegrationEnabledLoading = false;
		},
		getSwissPostOrdersRequest: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostOrdersRequest>,
		) => {
			const shipmentsState = state;
			shipmentsState.shipmentOrdersLoading = true;
			shipmentsState.requestDeliveryError = null;
			shipmentsState.firstMileLabelError = null;
		},
		getSwissPostOrdersSuccess: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostOrdersResponsePayload>,
		) => {
			const shipmentsState = state;
			shipmentsState.shipmentOrders = action.payload;
			shipmentsState.shipmentOrdersLoading = false;
		},
		getSwissPostOrderRequest: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostOrderRequest>,
		) => {
			const shipmentsState = state;
			shipmentsState.shipmentOrderLoading = true;
			shipmentsState.shipmentOrderError = null;
		},
		getSwissPostOrderRequestWithoutReload: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostOrderRequest>,
		) => {
			const shipmentsState = state;
			shipmentsState.shipmentOrderLoading = false;
			shipmentsState.shipmentOrderError = null;
		},

		getSwissPostOrderSuccess: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostOrderResponsePayload>,
		) => {
			const shipmentsState = state;
			shipmentsState.shipmentOrder = action.payload;
			shipmentsState.shipmentOrderLoading = false;
			shipmentsState.shipmentOrderError = null;
		},
		setSwissPostOrderError: (state: TShipmentsStore, action: PayloadAction<string | null>) => {
			const shipmentsState = state;
			shipmentsState.shipmentOrderError = action.payload;
			shipmentsState.shipmentOrderLoading = false;
		},
		getSwissPostDownloadLabelRequest: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostDownloadLabelRequest>,
		) => {
			const shipmentsState = state;
			shipmentsState.shipmentLabelLoading = true;
		},
		getSwissPostDownloadLabelSuccess: (state: TShipmentsStore) => {
			const shipmentsState = state;
			shipmentsState.shipmentLabelLoading = false;
		},
		getSwissPostDownloadProformaRequest: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostDownloadProformaRequest>,
		) => {
			const shipmentsState = state;
			shipmentsState.shipmentProformaLoading = true;
		},
		getSwissPostDownloadProformaSuccess: (state: TShipmentsStore) => {
			const shipmentsState = state;
			shipmentsState.shipmentProformaLoading = false;
		},
		uploadSwissPostProformaRequest: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostUploadProformaRequest>,
		) => {
			const shipmentsState = state;
			shipmentsState.shipmentProformaUploadLoading = true;
			shipmentsState.shipmentProformaUploadError = null;
		},
		uploadSwissPostProformaSuccess: (state: TShipmentsStore) => {
			const shipmentsState = state;
			shipmentsState.shipmentProformaUploadLoading = false;
		},
		setSwissPostProformaUploadError: (
			state: TShipmentsStore,
			action: PayloadAction<string | null>,
		) => {
			const shipmentsState = state;
			shipmentsState.shipmentProformaUploadError = action.payload;
			shipmentsState.shipmentProformaUploadLoading = false;
		},
		getSwissPostUnsignedProformaRequest: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostDownloadProformaRequest>,
		) => {
			const shipmentsState = state;
			shipmentsState.unsignedProforma = null;
			shipmentsState.unsignedProformaLoading = true;
		},
		getSwissPostUnsignedProformaSuccess: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostDownloadProformaResponsePayload>,
		) => {
			const shipmentsState = state;
			shipmentsState.unsignedProforma = action.payload;
			shipmentsState.unsignedProformaLoading = false;
		},
		getSwissPostSignedProformaPreviewRequest: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostSignedProformaPreviewRequest>,
		) => {
			const shipmentsState = state;
			shipmentsState.signedProformaPreview = null;
			shipmentsState.signedProformaPreviewLoading = true;
		},
		getSwissPostSignedProformaPreviewSuccess: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostSignedProformaPreviewResponsePayload>,
		) => {
			const shipmentsState = state;
			shipmentsState.signedProformaPreview = action.payload;
			shipmentsState.signedProformaPreviewLoading = false;
		},
		clearSwissPostSignedProformaPreview: (state: TShipmentsStore) => {
			const shipmentsState = state;
			shipmentsState.signedProformaPreview = null;
		},
		clearSwissPostProformaSigning: (state: TShipmentsStore) => {
			const shipmentsState = state;
			shipmentsState.unsignedProforma = null;
			shipmentsState.signedProformaPreview = null;
		},
		requestSwissPostDelivery: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostRequestDeliveryRequest>,
		) => {
			const shipmentsState = state;
			shipmentsState.requestDeliveryLoading = true;
			shipmentsState.requestDeliveryError = null;
		},
		requestSwissPostDeliverySuccess: (state: TShipmentsStore) => {
			const shipmentsState = state;
			shipmentsState.requestDeliveryLoading = false;
			shipmentsState.requestDeliveryError = null;
		},
		setRequestDeliveryError: (state: TShipmentsStore, action: PayloadAction<string | null>) => {
			const shipmentsState = state;
			shipmentsState.requestDeliveryError = action.payload;
			shipmentsState.requestDeliveryLoading = false;
		},
		sendSwissPostLastErrorByEmailRequest: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostSendLastErrorByEmailRequest>,
		) => {
			const shipmentsState = state;
			shipmentsState.sendLastErrorByEmailLoading = true;
			shipmentsState.sendLastErrorByEmailSuccess = false;
			shipmentsState.sendLastErrorByEmailError = null;
		},
		sendSwissPostLastErrorByEmailSuccess: (state: TShipmentsStore) => {
			const shipmentsState = state;
			shipmentsState.sendLastErrorByEmailLoading = false;
			shipmentsState.sendLastErrorByEmailSuccess = true;
			shipmentsState.sendLastErrorByEmailError = null;
		},
		setSendSwissPostLastErrorByEmailError: (
			state: TShipmentsStore,
			action: PayloadAction<string | null>,
		) => {
			const shipmentsState = state;
			shipmentsState.sendLastErrorByEmailError = action.payload;
			shipmentsState.sendLastErrorByEmailLoading = false;
			shipmentsState.sendLastErrorByEmailSuccess = false;
		},
		sendSwissPostLastErrorByEmailInit: (state: TShipmentsStore) => {
			const shipmentsState = state;
			shipmentsState.sendLastErrorByEmailLoading = false;
			shipmentsState.sendLastErrorByEmailSuccess = false;
			shipmentsState.sendLastErrorByEmailError = null;
		},
		updateSwissPostHsCodeRequest: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostUpdateHsCodeResponse>,
		) => {
			const shipmentsState = state;
			shipmentsState.updateHsCodeLoading = true;
		},
		updateSwissPostHsCodeSuccess: (state: TShipmentsStore) => {
			const shipmentsState = state;
			shipmentsState.updateHsCodeLoading = false;
		},
		getHsCodeDescriptionRequest: (
			state: TShipmentsStore,
			action: PayloadAction<THsCodeDescriptionRequest>,
		) => {
			const shipmentsState = state;
			shipmentsState.hsCodeDescriptionLoading = true;
		},
		getHsCodeDescriptionSuccess: (
			state: TShipmentsStore,
			action: PayloadAction<THsCodeDescriptionResponsePayload>,
		) => {
			const shipmentsState = state;
			shipmentsState.hsCodeDescription = action.payload;
			shipmentsState.hsCodeDescriptionLoading = false;
		},
		updateOrderFieldValuesRequest: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostUpdateOrderFieldValueRequest>,
		) => {
			const shipmentsState = state;
			shipmentsState.updateOrderFieldValueLoading = true;
		},
		updateOrderFieldValuesSuccess: (state: TShipmentsStore) => {
			const shipmentsState = state;
			shipmentsState.updateOrderFieldValueLoading = false;
		},
		updateCustomsDescriptionsRequest: (
			state: TShipmentsStore,
			action: PayloadAction<TUpdateCustomsDescriptionRequest>,
		) => {
			const shipmentsState = state;
			shipmentsState.updateCustomsDescriptionsLoading = true;
		},
		updateCustomsDescriptionsSuccess: (state: TShipmentsStore) => {
			const shipmentsState = state;
			shipmentsState.updateCustomsDescriptionsLoading = false;
		},
		updateSwissPostWeightRequest: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostUpdateWeightRequest>,
		) => {
			const shipmentsState = state;
			shipmentsState.updateWeightLoading = true;
			shipmentsState.updateWeightError = null;
		},
		updateSwissPostWeightSuccess: (state: TShipmentsStore) => {
			const shipmentsState = state;
			shipmentsState.updateWeightLoading = false;
		},
		updateSwissPostWeightFailure: (state: TShipmentsStore, action: PayloadAction<string>) => {
			const shipmentsState = state;
			shipmentsState.updateWeightLoading = false;
			shipmentsState.updateWeightError = { productId: action.payload, ts: Date.now() };
		},
		getFirstMileLabelRequest: (
			state: TShipmentsStore,
			action: PayloadAction<TSwissPostFirstMileLabelRequest>,
		) => {
			const shipmentsState = state;
			shipmentsState.firstMileLabelLoading = true;
			shipmentsState.firstMileLabelErrorDialog = null;
			shipmentsState.firstMileLabelError = null;
		},
		getFirstMileLabelSuccess: (state: TShipmentsStore) => {
			const shipmentsState = state;
			shipmentsState.firstMileLabelLoading = false;
			shipmentsState.firstMileLabelErrorDialog = null;
			shipmentsState.firstMileLabelError = null;
		},
		setFirstMileLabelErrorDialog: (
			state: TShipmentsStore,
			action: PayloadAction<string | null>,
		) => {
			const shipmentsState = state;
			shipmentsState.firstMileLabelErrorDialog = action.payload;
			shipmentsState.firstMileLabelLoading = false;
		},
		setFirstMileLabelError: (state: TShipmentsStore, action: PayloadAction<string | null>) => {
			const shipmentsState = state;
			shipmentsState.firstMileLabelError = action.payload;
			shipmentsState.firstMileLabelLoading = false;
		},
		shipmentsInitState: () => initialState,
	},
});

export default shipments.reducer;
export const {
	getIsSwissPostIntegrationEnabledRequest,
	getIsSwissPostIntegrationEnabledSuccess,
	getSwissPostOrdersRequest,
	getSwissPostOrdersSuccess,
	getSwissPostOrderRequest,
	getSwissPostOrderSuccess,
	setSwissPostOrderError,
	getSwissPostDownloadLabelRequest,
	getSwissPostDownloadLabelSuccess,
	getSwissPostDownloadProformaRequest,
	getSwissPostDownloadProformaSuccess,
	requestSwissPostDelivery,
	requestSwissPostDeliverySuccess,
	setRequestDeliveryError,
	sendSwissPostLastErrorByEmailRequest,
	sendSwissPostLastErrorByEmailSuccess,
	setSendSwissPostLastErrorByEmailError,
	sendSwissPostLastErrorByEmailInit,
	updateSwissPostHsCodeRequest,
	updateSwissPostHsCodeSuccess,
	getHsCodeDescriptionRequest,
	getHsCodeDescriptionSuccess,
	updateOrderFieldValuesRequest,
	updateOrderFieldValuesSuccess,
	updateCustomsDescriptionsRequest,
	updateCustomsDescriptionsSuccess,
	updateSwissPostWeightRequest,
	updateSwissPostWeightSuccess,
	updateSwissPostWeightFailure,
	getSwissPostOrderRequestWithoutReload,
	uploadSwissPostProformaRequest,
	uploadSwissPostProformaSuccess,
	setSwissPostProformaUploadError,
	getSwissPostUnsignedProformaRequest,
	getSwissPostUnsignedProformaSuccess,
	getSwissPostSignedProformaPreviewRequest,
	getSwissPostSignedProformaPreviewSuccess,
	clearSwissPostSignedProformaPreview,
	clearSwissPostProformaSigning,
	shipmentsInitState,
	getFirstMileLabelRequest,
	getFirstMileLabelSuccess,
	setFirstMileLabelErrorDialog,
	setFirstMileLabelError,
} = shipments.actions;
